import React from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'clsx';
import queryString from 'query-string';

import Button from '../../../../library/button/button';
import MinimumPriceMessage from '../../../../components/minimum-price-message/minimum-price-message';
import Price from '../../../../components/price/price';
import ProductDescription from '../../../../components/product-description/product-description';
import ProductFlags from '../../../../components/product-flags/product-flags';
import ProductImage from '../../../../components/product-image/product-image';
import ProductSecondaryFlags from '../../../../components/product-secondary-flags/product-secondary-flags';
import StackedMarkdownPrice from '../../../../components/stacked-markdown-price/stacked-markdown-price';
import WinterTireBadge from '../../../../components/winter-tire-badge/winter-tire-badge';
import { TimesRegularIcon } from '../../../../shared/icons/icons';

import {
  useContainerQuery,
  useMyAccount,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import {
  getIsNoLongerAvailable,
  getPriceInfo,
  getPrioritizedArticleStatusProduct,
  getShouldShowMinimumPriceMessage,
  isWinterTire
} from '../../../../shared/utils/product/product';

import { ProductData } from '../../../../shared/types/graphql-types';
import { CURRENT_SEARCH_OPTIONS } from '../../../../shared/constants/search-types';

import styles from './product-compare-preview-summary.scss';

interface ProductComparePreviewSummaryProps {
  className?: string;
  onClearProduct?: (product: ProductData) => void;
  product: ProductData;
}

function ProductComparePreviewSummary(
  props: ProductComparePreviewSummaryProps
) {
  const { className, onClearProduct, product } = props;
  const { isMinMd } = useContainerQuery();
  const { isTreadwellFlow, treadwellPreferences } = useTreadwellPreferences();
  const { isFleetAccount } = useMyAccount();
  const { search } = useLocation();
  const { currentSearchOption } = queryString.parse(search);
  const isTreadwellSearchOption =
    currentSearchOption === CURRENT_SEARCH_OPTIONS.TREADWELL;

  const isStaggered = Boolean(product?.rearProduct);
  const isWinterOnly = isWinterTire(product);
  const priceInfo = getPriceInfo(product);
  const rearPriceInfo = getPriceInfo(product?.rearProduct);
  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    product?.rearProduct
  );
  const isNoLongerAvailable = getIsNoLongerAvailable(articleStatusProduct);
  const showMinimumPriceMessage = getShouldShowMinimumPriceMessage(product);
  const showRearMinimumPriceMessage = getShouldShowMinimumPriceMessage(
    product?.rearProduct
  );
  const isWinterTreadwellFlow = Boolean(treadwellPreferences?.showWinterTires);

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  return (
    <div
      className={cn(
        styles['container'],
        `${className || ''} product-compare-preview-summary`
      )}
    >
      {product && (
        <div>
          <div className={styles['flag-container']}>
            <div>
              <ProductFlags
                hideLabel
                hideTreadwellRank={!isTreadwellFlow}
                product={product}
              />
              {process.env.__FF_FLEET_ACCOUNT__ === 'true' && (
                <ProductSecondaryFlags
                  hideLabel
                  hideWebOnly
                  product={product}
                />
              )}
            </div>

            {isWinterTreadwellFlow && isTreadwellSearchOption && (
              <div className={styles['winter-tire-badge']}>
                <WinterTireBadge isWinterOnly={isWinterOnly} />
              </div>
            )}
          </div>

          <Button
            className={styles['delete-button']}
            icon={TimesRegularIcon}
            onClick={() => onClearProduct?.(product)}
          />
        </div>
      )}
      {product && (
        <div>
          <ProductImage
            className={styles['product-image']}
            height={isMinMd ? 72 : 40}
            imageFormatType="PRODUCT_X_SMALL"
            product={product}
            width={isMinMd ? 72 : 40}
          />

          <div className={styles['description']}>
            <ProductDescription
              brand={product?.brand}
              className={styles['product-description']}
              name={product?.name}
            />
            {!isNoLongerAvailable && (
              <div
                className={
                  isStaggered
                    ? styles['price-container-staggered']
                    : styles['price-container']
                }
              >
                <div className={styles['price-info']}>
                  <StackedMarkdownPrice
                    hidePercentageSaved={isFleetEnabled}
                    product={product}
                  />
                  <div className={styles['price-item']}>
                    {isStaggered && (
                      <div className={styles['price-label']}>F:&nbsp;</div>
                    )}

                    <Price
                      {...priceInfo}
                      isPriceForEach
                      seePriceInCartMessage="Price in cart"
                      unitName={product?.unitName}
                    />
                  </div>
                </div>
                {isStaggered && (
                  <div className={styles['price-info']}>
                    <StackedMarkdownPrice
                      hidePercentageSaved={isFleetEnabled}
                      product={product?.rearProduct}
                    />
                    <div className={styles['price-item']}>
                      <div className={styles['price-label']}>R:&nbsp;</div>

                      <Price
                        {...rearPriceInfo}
                        isPriceForEach
                        seePriceInCartMessage="Price in cart"
                        unitName={product?.unitName}
                      />
                    </div>
                  </div>
                )}

                {(showMinimumPriceMessage || showRearMinimumPriceMessage) && (
                  <MinimumPriceMessage
                    message={
                      product?.mapDisplayRule?.message ||
                      product.rearProduct?.mapDisplayRule?.message
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductComparePreviewSummary;
